'use client';

import { useState } from 'react';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { H3 } from '@osmosis/tailwind-ui/components/Text';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { faAngleDown } from '@fortawesome/pro-light-svg-icons';
import { text0 } from '@osmosis/tailwind-ui/styles/text';
import { Button } from '@osmosis/tailwind-ui/components/Button';

type Props = {
  title: string;
  children: React.ReactNode;
  className?: string;
  labels?: {
    expand: string;
    collapse: string;
  };
};

/**
 * CollapsibleColumn Component.
 */
const CollapsibleColumn = ({
  title,
  children,
  className = '',
  labels,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const columnId = `footer-col-${title.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <Box
      className={`w-full basis-full border-t border-white/20 md:basis-1/3 md:border-0 md:py-0 md:pr-4 lg:pr-8 ${className}`}
      style={{ flexGrow: 1 }}
    >
      {/* Mobile: full-row clickable button — announced as button by screen readers */}
      <Button
        variant="transparent"
        data-testid={`footer-column-toggle-${title
          .replace(/\s+/g, '-')
          .toLowerCase()}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-controls={columnId}
        aria-label={isOpen ? labels?.collapse : labels?.expand}
        className="flex w-full items-center justify-between bg-transparent p-0 py-8 text-left hover:bg-transparent active:bg-transparent md:hidden"
      >
        <H3
          className={` font-national-2 text-lg ${text0} font-medium leading-[1.6] md:py-0 md:leading-[32px]`}
          data-testid="footer-column-header"
        >
          {title}
        </H3>
        <Box>
          <Icon
            size="lg"
            icon={faAngleDown}
            className={`shrink-0 transition-all ${text0} ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </Box>
      </Button>

      {/* Desktop: static heading — no button semantics for screen readers */}
      <H3
        className={`hidden ${text0} font-national-2 font-medium sm:text-xl sm:leading-[32px] md:mb-2 md:block`}
        data-testid="footer-column-header"
      >
        {title}
      </H3>

      {/* Content — md:block overrides the state-based hidden/block on desktop */}
      <Box
        id={columnId}
        className={`${isOpen ? 'block' : 'hidden'} transition-all md:block`}
      >
        {children}
      </Box>
    </Box>
  );
};

export default CollapsibleColumn;
